import { useState, useRef } from 'react';
import { Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

interface UseImageCropProps {
  onCropComplete?: (croppedImage: File) => void;
}

export const useImageCrop = ({ onCropComplete }: UseImageCropProps = {}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    width: 100,
    height: 100,
    x: 0,
    y: 0,
  });
  const [completedCrop, setCompletedCrop] = useState<Crop | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState<string | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setTempImageUrl(reader.result as string);
        setCrop({ unit: 'px', width: 0, height: 0, x: 0, y: 0 });
        setCompletedCrop(null);
        setIsModalOpen(true);
        e.target.value = '';
      };
      reader.readAsDataURL(file);
    }
  };

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    const size = Math.min(width, height);
    const crop: Crop = {
      unit: 'px',
      width: size,
      height: size,
      x: (width - size) / 2,
      y: (height - size) / 2,
    };
    setCrop(crop);
    setCompletedCrop(crop);
  };

  const getCroppedImg = async (image: HTMLImageElement, crop: Crop): Promise<File | null> => {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const pixelRatio = window.devicePixelRatio;

    if (crop.width && crop.height && crop.x !== undefined && crop.y !== undefined) {
      canvas.width = crop.width * pixelRatio;
      canvas.height = crop.height * pixelRatio;
      const ctx = canvas.getContext('2d');

      if (ctx) {
        ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
        ctx.imageSmoothingQuality = 'high';

        ctx.drawImage(
          image,
          crop.x * scaleX,
          crop.y * scaleY,
          crop.width * scaleX,
          crop.height * scaleY,
          0,
          0,
          crop.width,
          crop.height,
        );

        return new Promise(resolve => {
          canvas.toBlob(
            blob => {
              if (blob) {
                const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
                resolve(file);
              } else {
                resolve(null);
              }
            },
            'image/jpeg',
            1,
          );
        });
      }
    }
    return null;
  };

  const handleCropComplete = async () => {
    if (imgRef.current && completedCrop?.width && completedCrop?.height) {
      try {
        const croppedImage = await getCroppedImg(imgRef.current, completedCrop);
        if (croppedImage) {
          const croppedImageUrl = URL.createObjectURL(croppedImage);
          setPreviewUrl(croppedImageUrl);
          onCropComplete?.(croppedImage);
          setIsModalOpen(false);
          setTempImageUrl(null);
        }
      } catch (e) {
        console.error('Error cropping image:', e);
      }
    }
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    setTempImageUrl(null);
    setCrop({
      unit: '%',
      width: 100,
      height: 100,
      x: 0,
      y: 0,
    });
  };

  return {
    previewUrl,
    crop,
    setCrop,
    completedCrop,
    setCompletedCrop,
    isModalOpen,
    tempImageUrl,
    imgRef,
    handleFileChange,
    onImageLoad,
    handleCropComplete,
    handleModalCancel,
  };
};
